import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "@majourneys/icons";
import { RTL_LANGS, getMoroccoNow } from "@majourneys/shared";
import { normalizeLocale } from "../lib/locale";
import { PW, pewterButton, pewterPrimary } from "../internal-ui/pewter-shell";
import {
  addDaysIso,
  compareMonths,
  isIsoDate,
  monthCells,
  monthOf,
  nextRange,
  nightsBetween,
  shiftMonth,
  stayPresets,
  type MonthRef,
  type StayPresetId,
  type StayRange,
} from "../lib/stay-range";
import { BEVEL_CHIP_ON } from "@majourneys/shared/ui";

/**
 * The stay date range picker (owner-approved design #53): tap a check-in, tap
 * a check-out, and the nights between fill with a soft tint while the two ends
 * wear the navy circle. The header counts the nights live, past days are
 * struck out, and an optional primary ("Show hotels") stays off until a full
 * range exists.
 *
 * Shared by every check-in / check-out surface: the Stays search popup, the
 * property sidebar and phone sheet (LiteAPI hotels), and the camp booking
 * sidebar and sheet (camps are stays). Never a native date input.
 *
 * Styles are mostly inline objects because the package also serves the city
 * sites, which do not load the web app's stylesheet; the few keyframes it
 * needs ship in the component's own <style>.
 */

export interface StayRangePickerProps {
  readonly checkIn: string | null;
  readonly checkOut: string | null;
  readonly onChange: (next: StayRange) => void;
  /** Shortest stay. Check-outs closer than this are greyed (villas and camps set one). */
  readonly minNights?: number;
  /** Quick ranges: This weekend, Next weekend, 1 week. On by default. */
  readonly presets?: boolean;
  /**
   * Months side by side. "auto" shows two once the picker itself is at least
   * TWO_MONTHS_MIN_WIDTH wide (a desktop popup) and one otherwise (a phone).
   */
  readonly months?: 1 | 2 | "auto";
  /** The primary under the calendar, off until a full range exists. */
  readonly primaryLabel?: string;
  readonly onPrimary?: () => void;
  /** Overrides i18n.language (tests, city sites). */
  readonly lang?: string;
}

/** Two 7 x 40px months plus their gap need about this much room. */
export const TWO_MONTHS_MIN_WIDTH = 600;

const DISABLED_INK = "#b8bacb";
/** A chosen range or day is a chip: the bevel face, no drop shadow (owner, 2026-10-11). */
const CHOSEN_SHADOW = BEVEL_CHIP_ON;

const PICKER_CSS = `
@keyframes mj-srp-pop { from { opacity: 0; transform: scale(0.7); } to { opacity: 1; transform: none; } }
@keyframes mj-srp-in { from { opacity: 0; transform: translateX(var(--mj-srp-from, 0)); } to { opacity: 1; transform: none; } }
@keyframes mj-srp-fade { from { opacity: 0; } to { opacity: 1; } }
.mj-srp-pop { animation: mj-srp-pop 200ms cubic-bezier(0.23, 1, 0.32, 1) both; }
.mj-srp-in { animation: mj-srp-in 220ms cubic-bezier(0.23, 1, 0.32, 1) both; }
.mj-srp-day:focus-visible { outline: 2px solid ${PW.navy}; outline-offset: -2px; border-radius: 999px; }
.mj-srp-day:not(:disabled):hover .mj-srp-face { background: rgba(17,14,57,0.06); }
.mj-srp-nav:not(:disabled):hover { background: rgba(17,14,57,0.06); }
@media (prefers-reduced-motion: reduce) {
  .mj-srp-pop, .mj-srp-in { animation: mj-srp-fade 160ms ease both; }
}
`;

/** Layout effect in the browser (no flash), plain effect on the server (no warning). */
const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

function isoAt(ref: MonthRef, day: number): string {
  return `${ref.year}-${String(ref.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function StayRangePicker({
  checkIn,
  checkOut,
  onChange,
  minNights = 1,
  presets = true,
  months = "auto",
  primaryLabel,
  onPrimary,
  lang: langProp,
}: StayRangePickerProps) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLocale(langProp ?? i18n.language);
  const rtl = RTL_LANGS.includes(lang);
  // Latin digits in every locale, Arabic included (CLAUDE.md, Web i18n).
  const locale = `${lang}-u-nu-latn`;

  /**
   * Today in Morocco, read only in the browser after mount. The server renders
   * in UTC and the visitor hydrates in their own zone, so a "today" picked
   * during render could differ between the two and abort hydration (React
   * #418, the date strip in memory). Until it is known the grid is not drawn.
   */
  const [today, setToday] = useState<string | null>(null);
  useIsoLayoutEffect(() => {
    setToday(getMoroccoNow().date);
  }, []);

  const [view, setView] = useState<MonthRef | null>(() => (isIsoDate(checkIn) ? monthOf(checkIn) : null));
  const [slideFrom, setSlideFrom] = useState(0);
  useIsoLayoutEffect(() => {
    if (!today) return;
    const now = monthOf(today);
    setView((v) => (v && compareMonths(v, now) >= 0 ? v : now));
  }, [today]);

  /* How many months fit: measured, never guessed, and 1 on the server. */
  const rootRef = useRef<HTMLDivElement>(null);
  const [wide, setWide] = useState(false);
  useEffect(() => {
    if (months !== "auto") return;
    const el = rootRef.current;
    if (!el) return;
    const measure = () => setWide(el.getBoundingClientRect().width >= TWO_MONTHS_MIN_WIDTH);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [months]);
  const count = months === "auto" ? (wide ? 2 : 1) : months;

  const [hover, setHover] = useState<string | null>(null);
  const [focusIso, setFocusIso] = useState<string | null>(null);
  const keyboardMove = useRef(false);
  const gridRef = useRef<HTMLDivElement>(null);

  const fmt = useMemo(
    () => ({
      month: new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" }),
      day: new Intl.NumberFormat(locale),
      full: new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }),
      short: new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }),
      weekday: new Intl.DateTimeFormat(locale, { weekday: "narrow", timeZone: "UTC" }),
    }),
    [locale],
  );
  // Monday first: 1 January 2024 was a Monday.
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => fmt.weekday.format(new Date(Date.UTC(2024, 0, 1 + i)))),
    [fmt],
  );
  const utc = (iso: string) => new Date(`${iso}T00:00:00Z`);

  const nights = nightsBetween(checkIn, checkOut);
  const pendingOut = Boolean(checkIn && !checkOut);
  const minN = Math.max(1, minNights);

  function isDisabled(iso: string): boolean {
    if (!today || iso < today) return true;
    // While a check-out is pending, days too close to the check-in wait.
    if (pendingOut && checkIn && iso > checkIn && nightsBetween(checkIn, iso) < minN) return true;
    return false;
  }

  function pick(iso: string) {
    if (isDisabled(iso)) return;
    onChange(nextRange({ checkIn, checkOut }, iso, minN));
  }

  const atFirst = !today || !view || compareMonths(view, monthOf(today)) <= 0;
  function page(delta: number) {
    if (!view || (delta < 0 && atFirst)) return;
    setSlideFrom(delta * (rtl ? -12 : 12));
    setView(shiftMonth(view, delta));
  }

  /* Roving focus: one day is the tab stop; the arrows move it. */
  const visible = view ? Array.from({ length: count }, (_, k) => shiftMonth(view, k)) : [];
  const lastVisible = visible[visible.length - 1];
  const inView = (iso: string) =>
    !!view && !!lastVisible && compareMonths(monthOf(iso), view) >= 0 && compareMonths(monthOf(iso), lastVisible) <= 0;
  const tabStop = (() => {
    for (const c of [focusIso, checkOut, checkIn, today]) if (c && inView(c) && !isDisabled(c)) return c;
    if (!view) return null;
    for (const ref of visible) for (const c of monthCells(ref)) if (c && !isDisabled(c)) return c;
    return null;
  })();

  function onGridKey(e: KeyboardEvent<HTMLDivElement>) {
    const from = (e.target as HTMLElement).dataset?.iso;
    if (!from || !today) return;
    const step: Record<string, number> = {
      ArrowLeft: rtl ? 1 : -1,
      ArrowRight: rtl ? -1 : 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    let to: string | null = null;
    if (e.key in step) to = addDaysIso(from, step[e.key]!);
    else if (e.key === "PageUp" || e.key === "PageDown") {
      const r = shiftMonth(monthOf(from), e.key === "PageUp" ? -1 : 1);
      const last = new Date(Date.UTC(r.year, r.month + 1, 0)).getUTCDate();
      to = isoAt(r, Math.min(Number(from.slice(8)), last));
    } else return;
    e.preventDefault();
    if (to < today) to = today;
    keyboardMove.current = true;
    setFocusIso(to);
    if (view && lastVisible) {
      const m = monthOf(to);
      if (compareMonths(m, view) < 0) page(compareMonths(m, view));
      else if (compareMonths(m, lastVisible) > 0) page(compareMonths(m, lastVisible));
    }
  }
  useEffect(() => {
    if (!keyboardMove.current || !focusIso) return;
    keyboardMove.current = false;
    gridRef.current?.querySelector<HTMLButtonElement>(`[data-iso="${focusIso}"]`)?.focus();
  });

  const presetRanges = today && presets ? stayPresets(today, checkIn) : null;
  // A preset shorter than the minimum stay is left out rather than offered and refused.
  const presetIds = (["this_weekend", "next_weekend", "one_week"] as StayPresetId[]).filter(
    (id) => !presetRanges || nightsBetween(presetRanges[id].checkIn, presetRanges[id].checkOut) >= minN,
  );

  const status = nights
    ? t("calendar.range.nights", { count: nights })
    : checkIn
      ? t("calendar.range.pick_checkout")
      : t("calendar.range.pick_checkin");

  const bandEnd = checkOut ?? (pendingOut && hover && checkIn && hover > checkIn && !isDisabled(hover) ? hover : null);

  return (
    <div ref={rootRef} dir={rtl ? "rtl" : undefined} className="font-sans" data-months={count}>
      <style href="mj-stay-range-picker" precedence="default">
        {PICKER_CSS}
      </style>

      <div className="flex items-center justify-between gap-2">
        <span
          role="status"
          aria-live="polite"
          className="text-[13px] font-semibold tabular-nums"
          style={{ color: nights ? PW.navy : PW.muted }}
        >
          {status}
        </span>
        <div className="flex items-center gap-1">
          {[-1, 1].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => page(d)}
              disabled={d < 0 ? atFirst : !view}
              aria-label={d < 0 ? t("calendar.previous_month") : t("calendar.next_month")}
              data-testid={d < 0 ? "srp-prev" : "srp-next"}
              className="mj-srp-nav grid h-9 w-9 place-items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-30"
              style={{ ...pewterButton, color: PW.navy }}
            >
              {/* Back points to where the week starts: left, or right in RTL. */}
              <Icon
                name={(d < 0) !== rtl ? "ri-arrow-left-s-line" : "ri-arrow-right-s-line"}
                data-icon={(d < 0) !== rtl ? "left" : "right"}
                className="text-[18px]"
                aria-hidden="true"
              />
            </button>
          ))}
        </div>
      </div>

      {presetRanges ? (
        <div role="group" aria-label={t("calendar.range.presets_label")} className="mt-2.5 flex flex-wrap gap-1.5">
          {presetIds.map((id) => {
            const r = presetRanges[id];
            const on = r.checkIn === checkIn && r.checkOut === checkOut;
            return (
              <button
                key={id}
                type="button"
                aria-pressed={on}
                onClick={() => {
                  setView(monthOf(r.checkIn));
                  onChange(r);
                }}
                className="min-h-[32px] rounded-full px-3 text-[12.5px] font-semibold transition-colors"
                style={
                  on
                    ? { background: PW.navyGradient, color: "#fff", boxShadow: CHOSEN_SHADOW }
                    : { ...pewterButton, color: PW.navy }
                }
              >
                {t(`calendar.range.${id}`)}
              </button>
            );
          })}
        </div>
      ) : null}

      {/* Until today is known (first paint after mount) the grid holds its height empty. */}
      <div
        ref={gridRef}
        onKeyDown={onGridKey}
        className={`mt-3 grid gap-x-6 ${count === 2 ? "grid-cols-2" : "grid-cols-1"}`}
        style={{ minHeight: 300 }}
      >
        {today && view
          ? visible.map((ref) => {
              const label = fmt.month.format(new Date(Date.UTC(ref.year, ref.month, 1)));
              const cells = monthCells(ref);
              return (
                <div
                  key={`${ref.year}-${ref.month}`}
                  className="mj-srp-in min-w-0"
                  style={{ ["--mj-srp-from" as string]: `${slideFrom}px` }}
                >
                  <p className="m-0 mb-1.5 text-center text-[14px] font-bold" style={{ color: PW.navy }}>
                    {label}
                  </p>
                  <div
                    aria-hidden="true"
                    className="grid grid-cols-7 text-center text-[11px] font-semibold"
                    style={{ color: PW.muted }}
                  >
                    {weekDays.map((w, i) => (
                      <span key={i} className="py-1">
                        {w}
                      </span>
                    ))}
                  </div>
                  <div role="group" aria-label={label} className="grid grid-cols-7 gap-y-1">
                    {cells.map((iso, i) => {
                      if (!iso) return <span key={`pad-${i}`} aria-hidden="true" />;
                      const disabled = isDisabled(iso);
                      const past = iso < today;
                      const isStart = iso === checkIn;
                      const isEnd = iso === checkOut;
                      const isEdge = isStart || isEnd;
                      const between = !!checkIn && !!bandEnd && iso > checkIn && iso < bandEnd;
                      const col = i % 7;
                      const band = between
                        ? { start: col === 0, end: col === 6 }
                        : null;
                      const halfBand =
                        !!checkIn && !!bandEnd && ((isStart && col !== 6) || (iso === bandEnd && col !== 0));
                      return (
                        <button
                          key={iso}
                          type="button"
                          data-iso={iso}
                          disabled={disabled}
                          tabIndex={iso === tabStop ? 0 : -1}
                          aria-pressed={isEdge}
                          aria-label={fmt.full.format(utc(iso))}
                          onClick={() => {
                            setFocusIso(iso);
                            pick(iso);
                          }}
                          onMouseEnter={() => setHover(iso)}
                          onMouseLeave={() => setHover((h) => (h === iso ? null : h))}
                          className="mj-srp-day relative grid h-10 min-w-0 place-items-center text-[13.5px] font-semibold tabular-nums disabled:cursor-not-allowed"
                          style={{
                            color: isEdge ? "#fff" : disabled ? DISABLED_INK : PW.navy,
                            textDecoration: past ? "line-through" : undefined,
                          }}
                        >
                          {band ? (
                            <span
                              aria-hidden="true"
                              className="absolute inset-y-0.5"
                              style={{
                                background: PW.softTint,
                                insetInlineStart: 0,
                                insetInlineEnd: 0,
                                borderStartStartRadius: band.start ? 999 : 0,
                                borderEndStartRadius: band.start ? 999 : 0,
                                borderStartEndRadius: band.end ? 999 : 0,
                                borderEndEndRadius: band.end ? 999 : 0,
                              }}
                            />
                          ) : null}
                          {halfBand ? (
                            <span
                              aria-hidden="true"
                              className="absolute inset-y-0.5"
                              style={{
                                background: PW.softTint,
                                insetInlineStart: isStart ? "50%" : 0,
                                insetInlineEnd: isStart ? 0 : "50%",
                              }}
                            />
                          ) : null}
                          {isEdge ? (
                            <span
                              key={`${iso}-${isStart ? "in" : "out"}`}
                              aria-hidden="true"
                              className="mj-srp-pop absolute left-1/2 top-1/2 -ml-5 -mt-5 h-10 w-10 rounded-full"
                              style={{ background: PW.navyGradient, boxShadow: CHOSEN_SHADOW }}
                            />
                          ) : (
                            <span
                              aria-hidden="true"
                              className="mj-srp-face absolute left-1/2 top-1/2 -ml-5 -mt-5 h-10 w-10 rounded-full transition-colors"
                            />
                          )}
                          <span className="relative">{fmt.day.format(Number(iso.slice(8)))}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })
          : null}
      </div>

      {onPrimary && primaryLabel ? (
        <div
          className="mt-3 flex items-center justify-between gap-3 pt-3"
          style={{ boxShadow: `inset 0 1px 0 ${PW.hairline}` }}
        >
          <span className="flex min-w-0 flex-wrap items-center gap-1.5 text-[12.5px]" style={{ color: PW.muted }}>
            <span style={{ color: checkIn ? PW.navy : PW.muted }}>
              {checkIn ? fmt.short.format(utc(checkIn)) : t("calendar.range.checkin")}
            </span>
            <Icon name={rtl ? "ri-arrow-left-line" : "ri-arrow-right-line"} className="shrink-0 text-[12px]" aria-hidden="true" />
            <span style={{ color: checkOut ? PW.navy : PW.muted }}>
              {checkOut ? fmt.short.format(utc(checkOut)) : t("calendar.range.checkout")}
            </span>
          </span>
          <button
            type="button"
            onClick={onPrimary}
            disabled={!nights}
            className="mj-press inline-flex h-11 shrink-0 items-center justify-center rounded-full px-5 text-[14px] font-bold transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
            style={pewterPrimary}
          >
            {primaryLabel}
          </button>
        </div>
      ) : null}
    </div>
  );
}
